import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Globe, LogOut, RefreshCw, Mail, Calendar, CheckCircle, XCircle, AlertTriangle, Loader2, Cpu } from 'lucide-react';
import { API } from '@/lib/api';



export default function ProfilePage() {
  const { t, language, changeLanguage } = useLanguage();
  const { user, getHeaders, logout, updateUser } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [checkingAi, setCheckingAi] = useState(false);
  const de = language === 'de';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/profile`, { headers: getHeaders() });
        setProfile(res.data);
      } catch (e) { console.error(e); }
    };
    fetchProfile();
  }, []);

  const handleLanguageToggle = async () => {
    const newLang = language === 'de' ? 'en' : 'de';
    changeLanguage(newLang);
    try {
      await axios.put(`${API}/profile`, { language: newLang }, { headers: getHeaders() });
      updateUser({ language: newLang });
    } catch { /* continue */ }
  };

  const handleLogout = async () => {
    await logout();
    nav('/');
  };

  const checkAiStatus = async () => {
    setCheckingAi(true);
    try {
      const res = await axios.get(`${API}/admin/ai-status`, { headers: getHeaders() });
      setAiStatus(res.data);
    } catch { toast.error('Status-Check fehlgeschlagen'); }
    setCheckingAi(false);
  };

  const goals = profile?.onboarding?.goals || [];
  const goalLabels = {
    weight: language === 'de' ? 'Gewicht reduzieren' : 'Lose weight',
    energy: language === 'de' ? 'Mehr Energie' : 'More energy',
    hormones: language === 'de' ? 'Hormonbalance' : 'Hormone balance',
    habits: language === 'de' ? 'Bessere Gewohnheiten' : 'Better habits',
    muscle: language === 'de' ? 'Muskeln erhalten' : 'Maintain muscle',
    stress: language === 'de' ? 'Weniger Stress-Essen' : 'Less stress eating',
  };

  return (
    <div className="space-y-6 pb-24 lg:pb-8 max-w-2xl" data-testid="profile-page">
      <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{t('profile.title')}</h1>

      {/* User Info */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="profile-user-card">
        <div className="flex items-center gap-5">
          <Avatar className="h-16 w-16 border-2 border-terracotta/20">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-terracotta/10 text-terracotta font-display text-xl">{user?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <h2 className="font-display text-2xl font-medium">{user?.name}</h2>
            <p className="font-body text-sm text-foreground/50 flex items-center gap-1"><Mail size={14} /> {user?.email}</p>
          </div>
        </div>
      </Card>

      {/* Goals */}
      {goals.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="profile-goals-card">
          <h3 className="font-display text-lg font-medium mb-3">{language === 'de' ? 'Deine Ziele' : 'Your Goals'}</h3>
          <div className="flex flex-wrap gap-2">
            {goals.map(g => (
              <Badge key={g} className="bg-terracotta/10 text-terracotta border-0 rounded-full px-4 py-1.5 font-body text-sm">{goalLabels[g] || g}</Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Settings */}
      <Card className="bg-white border border-border/60 rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)]" data-testid="profile-settings-card">
        <h3 className="font-display text-lg font-medium mb-4">{language === 'de' ? 'Einstellungen' : 'Settings'}</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-foreground/40" />
              <div>
                <p className="font-body text-sm font-medium">{t('profile.language')}</p>
                <p className="font-body text-xs text-foreground/40">{language === 'de' ? 'Deutsch' : 'English'}</p>
              </div>
            </div>
            <Switch checked={language === 'en'} onCheckedChange={handleLanguageToggle} data-testid="profile-language-switch" />
          </div>
          <Separator />
          <button onClick={() => nav('/onboarding')} className="flex items-center gap-3 w-full py-2 group" data-testid="redo-onboarding-btn">
            <RefreshCw size={20} className="text-foreground/40 group-hover:text-terracotta transition-colors" />
            <span className="font-body text-sm font-medium group-hover:text-terracotta transition-colors">{t('profile.edit_onboarding')}</span>
          </button>
        </div>
      </Card>

      {/* AI Status (Admin only) */}
      {isAdmin && (
        <Card className={`border rounded-2xl p-6 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)] ${aiStatus?.overall === 'ok' ? 'bg-green-50/50 border-green-200' : aiStatus?.overall === 'issues' ? 'bg-red-50/50 border-red-200' : 'bg-white border-border/60'}`} data-testid="ai-status-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-medium flex items-center gap-2">
              <Cpu size={18} className="text-foreground/50" />
              {de ? 'KI-System Status' : 'AI System Status'}
            </h3>
            <Button onClick={checkAiStatus} disabled={checkingAi} variant="outline" size="sm" className="rounded-full px-4" data-testid="check-ai-btn">
              {checkingAi ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              <span className="ml-1.5">{checkingAi ? (de ? 'Prüfe...' : 'Checking...') : (de ? 'Jetzt prüfen' : 'Check now')}</span>
            </Button>
          </div>
          {!aiStatus ? (
            <p className="font-body text-sm text-foreground/40">{de ? 'Klicke "Jetzt prüfen" um den Status aller KI-Services zu testen.' : 'Click "Check now" to test all AI services.'}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(aiStatus.services).map(([key, svc]) => {
                const isOk = svc.status === 'ok';
                const isBudget = svc.status === 'budget';
                const Icon = isOk ? CheckCircle : isBudget ? AlertTriangle : XCircle;
                const color = isOk ? 'text-green-600' : isBudget ? 'text-amber-500' : 'text-red-500';
                const bg = isOk ? 'bg-green-50' : isBudget ? 'bg-amber-50' : 'bg-red-50';
                const labels = { claude: 'Claude (Chat & Ernährung)', openai_vision: 'OpenAI Vision (Selfie)', resend: 'Resend (E-Mail)', emergent_key: 'Emergent LLM Key' };
                return (
                  <div key={key} className={`flex items-center justify-between p-3 rounded-xl ${bg}`} data-testid={`ai-status-${key}`}>
                    <div className="flex items-center gap-3">
                      <Icon size={18} className={color} />
                      <div>
                        <p className="font-body text-sm font-medium">{labels[key] || key}</p>
                        {svc.model && <p className="font-body text-[10px] text-foreground/40">{svc.model}</p>}
                        {svc.error && <p className="font-body text-[10px] text-red-500 max-w-xs truncate">{svc.error}</p>}
                      </div>
                    </div>
                    <Badge className={`border-0 text-[10px] rounded-full ${isOk ? 'bg-green-100 text-green-700' : isBudget ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                      {isOk ? 'OK' : isBudget ? (de ? 'Budget!' : 'Budget!') : (de ? 'Fehler' : 'Error')}
                    </Badge>
                  </div>
                );
              })}
              {aiStatus.overall === 'issues' && (
                <p className="font-body text-xs text-red-500 mt-2">
                  {de ? 'Tipp: Budget-Probleme? Gehe zu Profile → Universal Key → Add Balance auf emergent.sh' : 'Tip: Budget issues? Go to Profile → Universal Key → Add Balance on emergent.sh'}
                </p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Logout */}
      <Button onClick={handleLogout} variant="outline" className="w-full rounded-full border-destructive/30 text-destructive hover:bg-destructive/5 py-5 font-body text-base" data-testid="profile-logout-btn">
        <LogOut size={18} className="mr-2" /> {t('profile.logout')}
      </Button>
    </div>
  );
}
